import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { hapticSelection } from '../../lib/haptics';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from '../ui/sheet';
import {
  Bell,
  Menu,
  Settings,
  GraduationCap,
  Link as LinkIcon,
  FileText,
  Megaphone,
  Activity,
  ShieldCheck,
  LogOut,
  ChevronRight,
} from 'lucide-react';

/**
 * En-tête mobile compact (masqué à partir de md). Branding à gauche (retour
 * accueil au tap), puis accès direct aux notifications et au menu secondaire
 * (« Plus ») à droite.
 *
 * Le menu « Plus » (paramètres, espace culte, documents, administration,
 * déconnexion…) vit ici, ce qui libère la barre d'onglets basse pour les
 * destinations principales (dont la Médiathèque).
 *
 * Hauteur volontairement réduite (safe-top-compact) pour ne pas gaspiller le
 * haut de l'écran ; alignée sur l'ancrage sticky des sous-onglets des hubs.
 */
export const MobileHeader = () => {
  const { user, userData, logout, isAdmin, isResponsable } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid),
      where('read', '==', false)
    );
    const unsub = onSnapshot(q, (snap) => setUnreadCount(snap.size));
    return () => unsub();
  }, [user]);

  const go = (to) => {
    hapticSelection();
    setMoreOpen(false);
    navigate(to);
  };

  const handleLogout = async () => {
    setMoreOpen(false);
    await logout();
    navigate('/');
  };

  // Liens du tiroir « Plus ». La Médiathèque n'y figure plus : elle est
  // désormais un onglet principal de la barre basse.
  const personalLinks = [
    { to: '/dashboard/settings', icon: Settings, label: 'Paramètres' },
    { to: '/dashboard/notifications', icon: Bell, label: 'Notifications', badge: unreadCount },
    { to: '/dashboard/learning', icon: GraduationCap, label: 'Espace Culte' },
    { to: '/dashboard/social-links', icon: LinkIcon, label: 'Réseaux sociaux' },
    { to: '/dashboard/documents', icon: FileText, label: 'Documents' },
  ];

  const adminLinks = [
    { to: '/dashboard/admin-notifications', icon: Megaphone, label: 'Diffusion' },
    { to: '/dashboard/audit', icon: Activity, label: 'Journal d’audit' },
  ];

  return (
    <header className="md:hidden bg-background/90 backdrop-blur-md border-b flex items-center gap-2 px-4 pb-2.5 sticky top-0 z-40 safe-top-compact">
      <button
        type="button"
        onClick={() => go('/dashboard')}
        className="flex items-center gap-2.5 min-w-0 flex-1 text-left"
        aria-label="Accueil"
      >
        <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center overflow-hidden shrink-0">
          <img src="/logo.jpg" alt="Logo" className="w-full h-full object-cover" />
        </div>
        <span className="font-semibold text-lg truncate">Daara Wakana</span>
      </button>

      <button
        type="button"
        onClick={() => go('/dashboard/notifications')}
        className="relative flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:bg-muted active:scale-95 transition-all shrink-0"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} non lues)` : ''}`}
      >
        <Bell className="h-[22px] w-[22px]" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      <button
        type="button"
        onClick={() => {
          hapticSelection();
          setMoreOpen(true);
        }}
        className="flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:bg-muted active:scale-95 transition-all shrink-0"
        aria-label="Menu"
      >
        <Menu className="h-[22px] w-[22px]" />
      </button>

      {/* Tiroir d'options (bottom sheet) — menu secondaire « Plus ». */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl max-h-[85vh] overflow-y-auto safe-bottom">
          <SheetHeader className="text-left">
            <SheetTitle asChild>
              <button
                type="button"
                onClick={() => go('/dashboard/settings')}
                className="flex w-full items-center gap-3 rounded-xl p-1 -m-1 text-left hover:bg-muted transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                  {userData?.fullName?.charAt(0) || 'U'}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">{userData?.fullName || 'Utilisateur'}</p>
                  <p className="text-xs font-normal text-muted-foreground capitalize">{userData?.role || 'Membre'} · Mon espace</p>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
              </button>
            </SheetTitle>
          </SheetHeader>

          <div className="mt-4 space-y-1">
            <p className="px-1 pb-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">Mon espace</p>
            {personalLinks.map((link) => (
              <DrawerLink key={link.to} link={link} onClick={() => go(link.to)} />
            ))}

            {(isAdmin || isResponsable) && (
              <>
                <p className="px-1 pt-4 pb-1 text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5" /> Administration
                </p>
                {adminLinks.map((link) => (
                  <DrawerLink key={link.to} link={link} onClick={() => go(link.to)} />
                ))}
              </>
            )}

            <SheetClose asChild>
              <button
                type="button"
                onClick={handleLogout}
                className="mt-3 flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30"
              >
                <LogOut className="h-5 w-5" />
                Déconnexion
              </button>
            </SheetClose>
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
};

const DrawerLink = ({ link, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-full items-center justify-between gap-3 rounded-xl px-3 py-3 text-sm font-medium text-foreground hover:bg-muted transition-colors"
  >
    <span className="flex items-center gap-3">
      <link.icon className="h-5 w-5 text-muted-foreground" />
      {link.label}
    </span>
    <span className="flex items-center gap-2">
      {link.badge > 0 && (
        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-bold text-white">
          {link.badge > 9 ? '9+' : link.badge}
        </span>
      )}
      <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
    </span>
  </button>
);

export default MobileHeader;
